<script lang="ts">
    import { App } from "@/constants";
    import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
    import { growaveConfigurationToken } from "@/tokens";
    import SimpleState from "@/utils/SimpleState";
    import ReviewsTheme from "@apps/reviews/components/ReviewsTheme/ReviewsTheme.svelte";
    import {
        SCROLL_QUESTIONS_TAB,
        SCROLL_REVIEWS_TAB,
    } from "@apps/reviews/constants";
    import ReviewsStorefrontValuesSettingsEntity from "@apps/reviews/entities/ReviewsStorefrontValuesSettingsEntity";
    import QuestionsQuery from "@apps/reviews/queries/QuestionsQuery";
    import ReviewsFilterQuery from "@apps/reviews/queries/ReviewsFilterQuery";
    import QuestionsService from "@apps/reviews/services/QuestionsService";
    import ReviewsFilterService from "@apps/reviews/services/ReviewsFilterService";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import { reviewsApplicationLoggerToken } from "@apps/reviews/tokens";
    import ReviewsQuery from "@apps/reviews/widgets/reviews_product_page_widget/queries/ReviewsQuery";
    import ReviewsService from "@apps/reviews/widgets/reviews_product_page_widget/services/ReviewsService";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import ScrollManager from "@lib/ScrollManager/ScrollManager";
    import { DesignModeProviderInterface } from "@modules/common_module/DesignModeProvider/DesignModeProviderInterface";
    import GrowaveStorefrontValuesService from "@modules/growave_storefront_values/services/GrowaveStorefrontValuesService";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import {
        Subscription,
        combineLatest,
        filter,
        lastValueFrom,
        map,
        switchMap,
        take,
        tap,
    } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import GrowaveBrand from "@components/GrowaveBrand/GrowaveBrand.svelte";
    import ImageViewer from "@components/ImageViewer/ImageViewer.svelte";
    import ImageViewerController from "@components/ImageViewer/ImageViewerController";
    import Portal from "@components/Portal/Portal.svelte";
    import PreviewBadge from "@components/PreviewBadge.svelte";
    import T from "@components/T.svelte";
    import Tabs from "@components/Tabs/Tabs.svelte";
    import TabsManager from "@components/Tabs/TabsManager";
    import TabsMenu from "@components/Tabs/TabsMenu.svelte";
    import TabsPanel from "@components/Tabs/TabsPanel.svelte";
    import Widget from "@components/Widget.svelte";

    import QuestionsListWidget from "../questions_list_widget/QuestionsListWidget";
    import ReviewsGalleryManager from "../reviews_gallery_widget/ReviewsGalleryManager";
    import ReviewsListHeaderWidget from "../reviews_list_header_widget/ReviewsListHeaderWidget";
    import ReviewsListWidget from "../reviews_list_widget/ReviewsListWidget";
    import ReviewsImagesSlider from "./components/ReviewsImagesSlider/ReviewsImagesSlider.svelte";
    import ReviewsImagesSliderQuery from "./queries/ReviewsImagesSliderQuery";
    import ReviewsImagesSliderService from "./services/ReviewsImagesSliderService";

    export let productIdManager: ProductIdManagerInterface;
    export let minImagesForSliderVisibility: number;
    export let reviewsPerPage: number;
    export let growaveStorefrontValuesService: GrowaveStorefrontValuesService;
    export let reviewsQuery: ReviewsQuery;
    export let reviewsService: ReviewsService;
    export let questionsQuery: QuestionsQuery;
    export let questionsService: QuestionsService;
    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let reviewsFilterQuery: ReviewsFilterQuery;
    export let reviewsFilterService: ReviewsFilterService;
    export let reviewsImagesSliderQuery: ReviewsImagesSliderQuery;
    export let reviewsImagesSliderService: ReviewsImagesSliderService;
    export let designModeProvider: DesignModeProviderInterface;
    export let reviewsGalleryManager: ReviewsGalleryManager;
    export let afterWidgetInject: () => void;

    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();
    const reviewsSettings$ =
        reviewsStorefrontValuesService.selectReviewsSettings();
    const watermarkSettings$ =
        growaveStorefrontValuesService.selectWatermarkSettings();
    const reviewsPaginationMeta$ = reviewsQuery.paginationMeta$;
    const questionsPaginationMeta$ = questionsQuery.paginationMeta$;
    const i18nModule = container.resolve(I18nModule);

    const tabsManager = new TabsManager();
    let reviewsWidgetRef: HTMLElement;
    const scrollManager = container.resolve(ScrollManager);

    const growaveConfiguration = container.resolve(growaveConfigurationToken);

    const isDesignMode = designModeProvider.isDesignMode() === true;

    const isReviewsAppEnabled =
        growaveConfiguration.availableApps.includes(App.Reviews) ||
        isDesignMode;

    const isQuestionsAppEnabled =
        growaveConfiguration.availableApps.includes(App.Questions) ||
        isDesignMode;

    const imageViewerController = new ImageViewerController();

    // TABS
    const tReviewsTabTitle$ = i18nModule.exports.i18n.selectT(
        "reviews.critical.ReviewsProductPageWidget",
        "reviews"
    );
    const tQuestionsTabTitle$ = i18nModule.exports.i18n.selectT(
        "reviews.critical.ReviewsProductPageWidget",
        "questions"
    );
    const tabs$ = combineLatest([
        reviewsPaginationMeta$,
        questionsPaginationMeta$,
        tReviewsTabTitle$,
        tQuestionsTabTitle$,
    ]).pipe(
        map(
            ([
                reviewsPaginationMeta,
                questionsPaginationMeta,
                tReviewsTabTitle,
                tQuestionsTabTitle,
            ]) => {
                const tabs = [];

                if (isReviewsAppEnabled) {
                    tabs.push({
                        name: "reviews",
                        label: tReviewsTabTitle,
                        tag: reviewsPaginationMeta.totalCount,
                    });
                }

                if (isQuestionsAppEnabled) {
                    tabs.push({
                        name: "questions",
                        label: tQuestionsTabTitle,
                        tag: questionsPaginationMeta.totalCount,
                    });
                }

                return tabs;
            }
        )
    );

    const showReviewsFilterUIState = new SimpleState<boolean>(false);

    const productId$ = productIdManager.productId$;
    const productReviewsImages$ = reviewsImagesSliderQuery.all$;

    onMount(() => {
        let settingsSubscription: Subscription;

        if (isReviewsAppEnabled) {
            settingsSubscription = reviewsSettings$
                .pipe(
                    filter(
                        (
                            settings
                        ): settings is ReviewsStorefrontValuesSettingsEntity =>
                            !!settings?.isProductReviewsWidgetEnabled
                    ),
                    switchMap((settings) =>
                        productId$.pipe(
                            filter(
                                (productId): productId is number =>
                                    productId !== null
                            ),
                            tap((productId) => {
                                lastValueFrom(
                                    reviewsImagesSliderService.getReviewsImagesSlider(
                                        {
                                            productId,
                                            sortingOptions: [
                                                settings.defaultSortingOption,
                                            ],
                                            onlyWithPhotos: true,
                                            perPage: 10,
                                        }
                                    )
                                );
                            })
                        )
                    )
                )
                .subscribe();
        }

        let questionAppProductIdSubscription: Subscription;

        if (isQuestionsAppEnabled) {
            questionAppProductIdSubscription = productId$
                .pipe(
                    filter(
                        (productId): productId is number => productId !== null
                    ),
                    tap((productId) => {
                        lastValueFrom(
                            questionsService.getQuestionsByProductId({
                                productId: productId,
                            })
                        );
                    })
                )
                .subscribe();
        }

        const tabsSubscription = tabs$.pipe(take(1)).subscribe((tabs) => {
            tabs.forEach((tab) => tabsManager.registerTab(tab.name));
        });

        scrollManager.register(SCROLL_REVIEWS_TAB, reviewsWidgetRef, () => {
            tabsManager.openTab("reviews");
        });

        scrollManager.register(SCROLL_QUESTIONS_TAB, reviewsWidgetRef, () => {
            tabsManager.openTab("questions");
        });

        afterWidgetInject();

        return () => {
            tabsSubscription.unsubscribe();
            scrollManager.unregister(SCROLL_REVIEWS_TAB);
            scrollManager.unregister(SCROLL_QUESTIONS_TAB);
            settingsSubscription?.unsubscribe();
            questionAppProductIdSubscription?.unsubscribe();
        };
    });
    const handleOpenImageReview = (
        event: CustomEvent<{
            reviewUid: string;
            activeImageIndex: number | undefined;
        }>
    ) => {
        reviewsGalleryManager.openReviewModal({
            reviews$: reviewsImagesSliderQuery.all$,
            activeReviewUid: event.detail.reviewUid,
            activeImageIndex: event.detail.activeImageIndex,
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_PRODUCT_PAGE_WIDGET,
        });
    };
</script>

<div class="gw-rv-product-page-widget-wrapper" bind:this={reviewsWidgetRef}>
    {#if $reviewsBranding$}
        <ReviewsTheme branding={$reviewsBranding$}>
            {#if $reviewsSettings$ && $reviewsSettings$.isProductReviewsWidgetEnabled}
                {#if isDesignMode}
                    <PreviewBadge />
                {/if}
                <div class="gw-rv-product-page-widget">
                    <div class="gw-rv-product-page-widget__title">
                        <h2
                            class="gw-rv-product-page-widget__title-text"
                            id="reviews"
                        >
                            {#if isReviewsAppEnabled}
                                <T
                                    namespace="reviews.critical.ReviewsProductPageWidget"
                                    translationKey="customer_reviews_title"
                                />
                            {:else if isQuestionsAppEnabled}
                                <T
                                    namespace="reviews.critical.ReviewsProductPageWidget"
                                    translationKey="questions_main_title"
                                />
                            {/if}
                        </h2>
                    </div>

                    <Widget
                        widget={ReviewsListHeaderWidget}
                        params={[
                            {
                                isWidgetShadowRootEnabled: false,
                                productIdManager,
                                reviewsService: reviewsService,
                            },
                            container.resolve(reviewsApplicationLoggerToken),
                        ]}
                    />

                    <div class="gw-rv-product-page-widget__main">
                        <Tabs
                            tabsName="rv-widget-layout-tab"
                            class="gw-rv-product-page-widget__tab"
                        >
                            <svelte:fragment slot="menu">
                                {#if $tabs$ && $tabs$.length >= 2}
                                    <TabsMenu
                                        {tabsManager}
                                        tabs={$tabs$}
                                        position="start"
                                    />
                                {/if}
                            </svelte:fragment>
                            <svelte:fragment slot="body">
                                {#if isReviewsAppEnabled}
                                    <TabsPanel {tabsManager} name={"reviews"}>
                                        <div
                                            class="gw-rv-product-page-widget__reviews-tab"
                                        >
                                            {#if $productReviewsImages$.length}
                                                <ReviewsImagesSlider
                                                    {reviewsImagesSliderService}
                                                    {reviewsImagesSliderQuery}
                                                    {minImagesForSliderVisibility}
                                                    productId={$productId$}
                                                    on:openImageReview={handleOpenImageReview}
                                                />
                                            {/if}

                                            <div
                                                class="gw-rv-product-page-widget__reviews-list"
                                            >
                                                <Widget
                                                    widget={ReviewsListWidget}
                                                    params={[
                                                        {
                                                            reviewsPerPage,
                                                            isWidgetShadowRootEnabled: false,
                                                            productIdManager,
                                                            reviewsService,
                                                            reviewsQuery,
                                                            reviewsFilterQuery,
                                                            reviewsFilterService,
                                                            showReviewsFilterUIState,
                                                            showAddReviewButton: false,
                                                            widgetName:
                                                                REVIEWS_WIDGET_NAMES.REVIEWS_PRODUCT_PAGE_WIDGET,
                                                            reviewsImagesSliderService:
                                                                reviewsImagesSliderService,
                                                        },
                                                        container.resolve(
                                                            reviewsApplicationLoggerToken
                                                        ),
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                    </TabsPanel>
                                {/if}
                                {#if isQuestionsAppEnabled}
                                    <TabsPanel {tabsManager} name={"questions"}>
                                        <div
                                            class="gw-rv-product-page-widget__questions-tab"
                                        >
                                            <Widget
                                                widget={QuestionsListWidget}
                                                params={[
                                                    {
                                                        isWidgetShadowRootEnabled: false,
                                                        productIdManager,
                                                        questionsService,
                                                        questionsQuery,
                                                        showAddQuestionButton: false,
                                                    },
                                                    container.resolve(
                                                        reviewsApplicationLoggerToken
                                                    ),
                                                ]}
                                            />
                                        </div>
                                    </TabsPanel>
                                {/if}
                            </svelte:fragment>
                        </Tabs>
                    </div>
                </div>

                <GrowaveBrand
                    class="gw-rv-main-widget__growave-brand"
                    canShowWatermark={$watermarkSettings$?.canShowWatermark}
                />
            {/if}
        </ReviewsTheme>
    {/if}
</div>

<Portal
    Component={ImageViewer}
    componentProps={{ imageViewerController }}
    selector="body"
    shadow
/>

<style lang="postcss" global>
    @import "../../../../themes/default/default.css";
    @import "../../../../themes/globals.css";

    .gw-rv-product-page-widget-wrapper {
        position: relative;

        &__growave-brand {
            text-align: right;
        }
    }

    .gw-rv-product-page-widget {
        &__main {
            margin: 1.5em 0;
            --gw-tabs-underline-color: var(--gw-rv-theme-primaryColor);
        }
        &__title {
            display: flex;
            align-items: center;
        }
        &__title-text {
            margin: 0;
            font-size: 1.125em;
            font-weight: 600;
        }
        &__sort {
            display: flex;
            justify-content: flex-end;
            margin-left: auto;
        }
        &__reviews-tab {
            margin-top: 1.25em;
        }
        &__questions-tab {
            margin-top: 1.25em;
        }
    }
</style>
